import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';
import { TooltipService } from './tooltip.service';

let _tipEl: HTMLElement | null = null;

function getTipEl(): HTMLElement {
  if (!_tipEl) {
    _tipEl = document.createElement('div');
    _tipEl.style.cssText = [
      'position:fixed',
      'z-index:99999',
      'padding:5px 10px',
      'border-radius:7px',
      "font-family:'Poppins',sans-serif",
      'font-size:11px',
      'font-weight:500',
      'line-height:1.45',
      'color:#e8ecf3',
      'background:rgba(10,14,32,0.93)',
      'border:1px solid rgba(232,236,243,0.14)',
      'backdrop-filter:blur(10px)',
      'box-shadow:0 4px 14px rgba(0,0,0,0.45)',
      'pointer-events:none',
      'max-width:220px',
      'white-space:normal',
      'opacity:0',
      'transition:opacity 0.13s ease',
      'display:none',
    ].join(';');
    document.body.appendChild(_tipEl);
  }
  return _tipEl;
}

@Directive({ selector: '[ssTooltip]', standalone: true })
export class TooltipDirective implements OnDestroy {
  @Input('ssTooltip') text = '';

  private readonly _el = inject(ElementRef<HTMLElement>);
  private readonly _svc = inject(TooltipService);
  private _showTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('mouseenter') show(): void {
    this._svc.set(this.text);
    if (!this.text?.trim()) return;
    this._cancelTimer();
    this._showTimer = setTimeout(() => this._show(), 380);
  }

  @HostListener('mouseleave') hide(): void {
    this._svc.clear();
    this._cancelTimer();
    this._hide();
  }

  @HostListener('click') click(): void {
    this._svc.clear();
    this._cancelTimer();
    this._hide();
  }

  ngOnDestroy(): void {
    this._svc.clear();
    this._cancelTimer();
    this._hide();
  }

  private _cancelTimer(): void {
    if (this._showTimer) { clearTimeout(this._showTimer); this._showTimer = null; }
  }

  private _show(): void {
    if (!this.text?.trim()) return;
    const tip = getTipEl();
    tip.textContent = this.text;
    tip.style.display = 'block';
    tip.style.opacity = '0';
    this._position(tip);
    requestAnimationFrame(() => { tip.style.opacity = '1'; });
  }

  private _hide(): void {
    const tip = getTipEl();
    tip.style.opacity = '0';
    setTimeout(() => { tip.style.display = 'none'; }, 140);
  }

  private _position(tip: HTMLElement): void {
    const rect = this._el.nativeElement.getBoundingClientRect();
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Force layout so we can measure the tip
    tip.style.left = '0';
    tip.style.top = '0';
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;

    // Prefer right, fall back to left, then below
    let left = rect.right + gap;
    let top  = rect.top + (rect.height - th) / 2;

    if (left + tw > vw - 8) {
      left = rect.left - tw - gap;
    }
    if (left < 8) {
      left = Math.max(8, rect.left);
      top  = rect.bottom + gap;
    }

    top = Math.max(8, Math.min(top, vh - th - 8));

    tip.style.left = `${Math.round(left)}px`;
    tip.style.top  = `${Math.round(top)}px`;
  }
}
